/* eslint-disable import/no-extraneous-dependencies */
import React, { useState } from 'react';
import Head from 'next/head';
import PropTypes from 'prop-types';
import { pdfjs } from 'react-pdf';
import { Container } from '@pwskills/rachnaui';
import Pw from '@pwskills/rachnaui/logo/Pw';
import Spinner from '@/components/common/Loader';
import { s3Url } from '@/utils/common/env.constants';
import useScreenWidth from '@/hooks/useScreenWidth';
import useUserDetails from '@/store/userDetails';
import { IcertificateDetails } from '../TypesAndInterfaces';
import {
  CertificateViewer,
  CertificateSidebar,
  LinkedInShareModal,
  CertificateSelectionModal,
} from './components';
import useCertificates from './hooks/useCertificates';
import {
  sendCertificatePageViewEvent,
  sendCertificateCtaClickedEvent,
  sendCertificateShareConfirmEvent,
} from './utils/certificateGAEvents';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

interface Props {
  certificateDetails: IcertificateDetails;
  setCertificateDetails: React.Dispatch<React.SetStateAction<IcertificateDetails>>;
  isCareerPath: boolean;
}

const CertificateDashboard = ({
  certificateDetails,
  setCertificateDetails,
  isCareerPath,
}: Props) => {
  const screenWidth = useScreenWidth();
  const [showLinkedInModal, setShowLinkedInModal] = useState(false);
  const [showFacebookModal, setShowFacebookModal] = useState(false);
  const [showTwitterModal, setShowTwitterModal] = useState(false);
  const { userDetails } = useUserDetails();
  const hasTrackedPageView = React.useRef(false);

  const {
    allCertificates,
    selectedIndex,
    setSelectedIndex,
    isCertLoading,
    setIsCertLoading,
    pageLoader,
    certificateImage,
  } = useCertificates({
    setCertificateDetails,
    isCareerPath,
  });

  // Track page view event once when certificates are loaded
  React.useEffect(() => {
    if (allCertificates.length > 0 && !hasTrackedPageView.current && userDetails?.username) {
      hasTrackedPageView.current = true;
      const partnerCertCount = allCertificates.filter((c) => c.isPartnerCertificate).length;
      sendCertificatePageViewEvent({
        certificates_count: allCertificates.length,
        certificate_ids: allCertificates.map((c) => c.certificate?.id).filter(Boolean),
        course_name: certificateDetails?.courseName,
        course_id: certificateDetails?.certificate?._id,
        partner_certs_available_count: partnerCertCount,
      });
    }
  }, [allCertificates, certificateDetails, userDetails]);

  // Actual share function
  const shareOnLinkedIn = (sharePageUrl: string, selectedCertIds: string[]) => {
    const partnerCertCount = allCertificates.filter((c) => c.isPartnerCertificate).length;
    sendCertificateShareConfirmEvent({
      sharing_medium: 'linkedin',
      certificate_shared_count: selectedCertIds.length,
      certificate_included: selectedCertIds,
      course_name: certificateDetails?.courseName,
      course_id: certificateDetails?.certificate?._id,
      partner_certs_available_count: partnerCertCount,
    });
    const message =
      selectedCertIds.length > 1
        ? `I'm excited to share that I've completed ${selectedCertIds.length} certificates from PW Skills including ${certificateDetails?.courseName}. Check them out!`
        : `I'm excited to share that I've completed the ${certificateDetails?.courseName} course from PW Skills.`;
    window.open(
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
        sharePageUrl
      )}&text=${encodeURIComponent(message)}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  // Handle LinkedIn share - show modal if multiple certificates, otherwise share directly
  const handleLinkedInShare = () => {
    sendCertificateCtaClickedEvent({
      click_text: 'Share on LinkedIn',
      course_name: certificateDetails?.courseName,
      course_id: certificateDetails?.certificate?._id,
      partner_certs_available_count: allCertificates.filter((c) => c.isPartnerCertificate).length,
    });
    if (allCertificates.length > 1) {
      setShowLinkedInModal(true);
    } else {
      // Single certificate - share directly
      shareOnLinkedIn(
        `${s3Url || ''}/learn/certificates/${allCertificates[0]?.certificate?.id}.pdf`,
        [allCertificates[0]?.certificate?.id]
      );
    }
  };

  // Facebook share function
  const shareOnFacebook = (sharePageUrl: string, selectedCertIds: string[]) => {
    const partnerCertCount = allCertificates.filter((c) => c.isPartnerCertificate).length;
    sendCertificateShareConfirmEvent({
      sharing_medium: 'facebook',
      certificate_shared_count: selectedCertIds.length,
      certificate_included: selectedCertIds,
      course_name: certificateDetails?.courseName,
      course_id: certificateDetails?.certificate?._id,
      partner_certs_available_count: partnerCertCount,
    });
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(sharePageUrl)}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  // Handle Facebook share - show modal if multiple certificates, otherwise share directly
  const handleFacebookShare = () => {
    sendCertificateCtaClickedEvent({
      click_text: 'Share on Facebook',
      course_name: certificateDetails?.courseName,
      course_id: certificateDetails?.certificate?._id,
      partner_certs_available_count: allCertificates.filter((c) => c.isPartnerCertificate).length,
    });
    if (allCertificates.length > 1) {
      setShowFacebookModal(true);
    } else {
      // Single certificate - share directly
      shareOnFacebook(
        `${s3Url || ''}/learn/certificates/${allCertificates[0]?.certificate?.id}.pdf`,
        [allCertificates[0]?.certificate?.id]
      );
    }
  };

  // Twitter share function
  const shareOnTwitter = (sharePageUrl: string, selectedCertIds: string[]) => {
    const partnerCertCount = allCertificates.filter((c) => c.isPartnerCertificate).length;
    sendCertificateShareConfirmEvent({
      sharing_medium: 'twitter',
      certificate_shared_count: selectedCertIds.length,
      certificate_included: selectedCertIds,
      course_name: certificateDetails?.courseName,
      course_id: certificateDetails?.certificate?._id,
      partner_certs_available_count: partnerCertCount,
    });
    window.open(
      `https://twitter.com/intent/tweet?url=${encodeURIComponent(sharePageUrl)}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  // Handle Twitter share - show modal if multiple certificates, otherwise share directly
  const handleTwitterShare = () => {
    sendCertificateCtaClickedEvent({
      click_text: 'Share on Twitter',
      course_name: certificateDetails?.courseName,
      course_id: certificateDetails?.certificate?._id,
      partner_certs_available_count: allCertificates.filter((c) => c.isPartnerCertificate).length,
    });
    if (allCertificates.length > 1) {
      setShowTwitterModal(true);
    } else {
      // Single certificate - share directly
      shareOnTwitter(
        `${s3Url || ''}/learn/certificates/${allCertificates[0]?.certificate?.id}.pdf`,
        [allCertificates[0]?.certificate?.id]
      );
    }
  };

  const returnChildren = () => {
    return (
      <div className="flex flex-col large:!flex-row w-full items-start justify-center overflow-auto gap-[24px]">
        <CertificateViewer
          certificateDetails={certificateDetails}
          allCertificates={allCertificates}
          selectedIndex={selectedIndex}
          onSelectCertificate={setSelectedIndex}
          isCareerPath={isCareerPath}
          isCertLoading={isCertLoading}
          onLoadSuccess={() => setIsCertLoading(false)}
        />
        <CertificateSidebar
          certificateDetails={certificateDetails}
          allCertificates={allCertificates}
          isCareerPath={isCareerPath}
          selectedIndex={selectedIndex}
          onLinkedInShare={handleLinkedInShare}
          onFacebookShare={handleFacebookShare}
          onTwitterShare={handleTwitterShare}
        />
      </div>
    );
  };

  if (!screenWidth) return null;

  if (screenWidth < 768)
    return (
      <div>
        <Head>
          <meta property="og:image" content={certificateImage} />
          <meta property="twitter:image" content={certificateImage} />
        </Head>
        <nav className="bg-black">
          <Container className="py-4 medium:w-[85%] medium:m-auto large:w-[95%]">
            <Pw />
          </Container>
        </nav>
        {pageLoader ? (
          <Spinner />
        ) : (
          <section className="flex bg-[#F8FAFB] items-start justify-center pt-[56px] px-4 medium:!px-[80px] min-h-screen">
            {returnChildren()}
          </section>
        )}
        <LinkedInShareModal
          isOpen={showLinkedInModal}
          onClose={() => setShowLinkedInModal(false)}
          allCertificates={allCertificates}
          selectedIndex={selectedIndex}
          onShare={shareOnLinkedIn}
        />
        <CertificateSelectionModal
          isOpen={showFacebookModal}
          onClose={() => setShowFacebookModal(false)}
          allCertificates={allCertificates}
          selectedIndex={selectedIndex}
          title="Share on Facebook"
          description="Select the certificate you want to share on Facebook"
          submitButtonText="Share"
          onSubmit={(selectedCertificates) => {
            const selectedCertIds = selectedCertificates.map((cert) => cert.certificate?.id);
            if (selectedCertIds.length === 0) return;

            // Generate share URL based on selection
            let shareUrl: string;
            if (selectedCertIds.length === 1) {
              shareUrl = `${s3Url || ''}/learn/certificates/${selectedCertIds[0]}.pdf`;
            } else {
              const baseUrl = `${window.location.origin}/learn/certificate/${selectedCertIds[0]}`;
              const queryParams = selectedCertIds
                .slice(1)
                .map((id, index) => `id${index + 1}=${id}`)
                .join('&');
              shareUrl = queryParams ? `${baseUrl}?${queryParams}` : baseUrl;
            }

            shareOnFacebook(shareUrl, selectedCertIds);
            setShowFacebookModal(false);
          }}
        />
        <CertificateSelectionModal
          isOpen={showTwitterModal}
          onClose={() => setShowTwitterModal(false)}
          allCertificates={allCertificates}
          selectedIndex={selectedIndex}
          title="Share on Twitter"
          description="Select the certificate you want to share on Twitter"
          submitButtonText="Share"
          onSubmit={(selectedCertificates) => {
            const selectedCertIds = selectedCertificates.map((cert) => cert.certificate?.id);
            if (selectedCertIds.length === 0) return;

            // Generate share URL based on selection
            let shareUrl: string;
            if (selectedCertIds.length === 1) {
              shareUrl = `${s3Url || ''}/learn/certificates/${selectedCertIds[0]}.pdf`;
            } else {
              const baseUrl = `${window.location.origin}/learn/certificate/${selectedCertIds[0]}`;
              const queryParams = selectedCertIds
                .slice(1)
                .map((id, index) => `id${index + 1}=${id}`)
                .join('&');
              shareUrl = queryParams ? `${baseUrl}?${queryParams}` : baseUrl;
            }

            shareOnTwitter(shareUrl, selectedCertIds);
            setShowTwitterModal(false);
          }}
        />
      </div>
    );
  return (
    <div>
      <Head>
        <meta property="og:image" content={certificateImage} />
        <meta property="twitter:image" content={certificateImage} />
      </Head>
      <nav className="bg-black">
        <Container className="py-4 medium:w-[85%] medium:m-auto large:w-[95%]">
          <Pw />
        </Container>
      </nav>
      {pageLoader ? (
        <Spinner />
      ) : (
        <section className="flex bg-[#F8FAFB] items-start justify-center pt-[56px] px-2 medium:!px-[80px] min-h-screen">
          <Container>{returnChildren()}</Container>
        </section>
      )}
      <LinkedInShareModal
        isOpen={showLinkedInModal}
        onClose={() => setShowLinkedInModal(false)}
        allCertificates={allCertificates}
        selectedIndex={selectedIndex}
        onShare={shareOnLinkedIn}
      />
      <CertificateSelectionModal
        isOpen={showFacebookModal}
        onClose={() => setShowFacebookModal(false)}
        allCertificates={allCertificates}
        selectedIndex={selectedIndex}
        title="Share on Facebook"
        description="Select the certificate you want to share on Facebook"
        submitButtonText="Share"
        onSubmit={(selectedCertificates) => {
          const selectedCertIds = selectedCertificates.map((cert) => cert.certificate?.id);
          if (selectedCertIds.length === 0) return;

          // Generate share URL based on selection
          let shareUrl: string;
          if (selectedCertIds.length === 1) {
            shareUrl = `${s3Url || ''}/learn/certificates/${selectedCertIds[0]}.pdf`;
          } else {
            const baseUrl = `${window.location.origin}/learn/certificate/${selectedCertIds[0]}`;
            const queryParams = selectedCertIds
              .slice(1)
              .map((id, index) => `id${index + 1}=${id}`)
              .join('&');
            shareUrl = queryParams ? `${baseUrl}?${queryParams}` : baseUrl;
          }

          shareOnFacebook(shareUrl, selectedCertIds);
          setShowFacebookModal(false);
        }}
        showSelectAll
        sharedMedium="facebook"
      />
      <CertificateSelectionModal
        isOpen={showTwitterModal}
        onClose={() => setShowTwitterModal(false)}
        allCertificates={allCertificates}
        selectedIndex={selectedIndex}
        title="Share on Twitter"
        description="Select the certificate you want to share on Twitter"
        submitButtonText="Share"
        onSubmit={(selectedCertificates) => {
          const selectedCertIds = selectedCertificates.map((cert) => cert.certificate?.id);
          if (selectedCertIds.length === 0) return;

          // Generate share URL based on selection
          let shareUrl: string;
          if (selectedCertIds.length === 1) {
            shareUrl = `${s3Url || ''}/learn/certificates/${selectedCertIds[0]}.pdf`;
          } else {
            const baseUrl = `${window.location.origin}/learn/certificate/${selectedCertIds[0]}`;
            const queryParams = selectedCertIds
              .slice(1)
              .map((id, index) => `id${index + 1}=${id}`)
              .join('&');
            shareUrl = queryParams ? `${baseUrl}?${queryParams}` : baseUrl;
          }

          shareOnTwitter(shareUrl, selectedCertIds);
          setShowTwitterModal(false);
        }}
        showSelectAll
        sharedMedium="twitter"
      />
    </div>
  );
};

CertificateDashboard.defaultProps = {
  certificateDetails: null,
};

CertificateDashboard.propTypes = {
  certificateDetails: PropTypes.instanceOf(Object),
};

export default CertificateDashboard;
